import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { loadUserManagementState } from "../utils/loadUserManagementState";

export type AuthUser = {
  id: string;
  username: string;
  email: string;
  name: string;
  surname: string;
  company: string;
  country: string;
  role: string;
  active: boolean;
  userGroups: string[];
  lastSeen: string | null;
  forcePasswordReset?: boolean;
};

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

type AuthState = {
  user: AuthUser | null;
  tokens: AuthTokens | null;
};

type AuthContextValue = AuthState & {
  setSession: (state: AuthState | null, options?: { remember?: boolean }) => void;
  logout: () => Promise<void>;
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  resolveApiPath: (value: string) => string;
  userManagementEnabled: boolean;
  userManagementResolved: boolean;
  applyUserManagementEnabled: (disabled: boolean) => void;
};

const storageKey = "sky-view.auth.session";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ADMIN_SESSION_USER: AuthUser = {
  id: "admin",
  username: "admin",
  email: "",
  name: "",
  surname: "",
  company: "",
  country: "",
  role: "admin",
  active: true,
  userGroups: [],
  lastSeen: null
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const sanitizeUser = useCallback(
    (user: AuthUser | null | undefined): AuthUser | null => {
      if (!user) {
        return null;
      }
      return {
        ...user,
        name: typeof user.name === "string" ? user.name : "",
        surname: typeof user.surname === "string" ? user.surname : "",
        company: typeof user.company === "string" ? user.company : "",
        country:
          typeof user.country === "string"
            ? user.country.trim().toUpperCase()
            : "",
        active: typeof (user as AuthUser).active === "boolean" ? (user as AuthUser).active : true,
        userGroups: Array.isArray(user.userGroups) ? user.userGroups : [],
        lastSeen:
          typeof (user as AuthUser).lastSeen === "string" && (user as AuthUser).lastSeen.trim() !== ""
            ? (user as AuthUser).lastSeen
            : null,
        forcePasswordReset: Boolean((user as AuthUser).forcePasswordReset)
      };
    },
    []
  );

  const storageRef = useRef<Storage | null>(null);

  const [state, setState] = useState<AuthState>(() => {
    if (typeof window === "undefined") {
      return { user: null, tokens: null };
    }

    const readFrom = (storage: Storage): AuthState | null => {
      const raw = storage.getItem(storageKey);
      if (!raw) {
        return null;
      }

      try {
        const parsed = JSON.parse(raw) as AuthState;
        storageRef.current = storage;
        return {
          user: sanitizeUser(parsed?.user),
          tokens: parsed?.tokens ?? null
        };
      } catch {
        return null;
      }
    };

    return readFrom(window.localStorage) ?? readFrom(window.sessionStorage) ?? { user: null, tokens: null };
  });

  const setSession = useCallback(
    (value: AuthState | null, options?: { remember?: boolean }) => {
      const nextState: AuthState = value
        ? { user: sanitizeUser(value.user), tokens: value.tokens ?? null }
        : { user: null, tokens: null };
      setState(nextState);
      if (typeof window === "undefined") {
        return;
      }

      if (!value) {
        window.localStorage.removeItem(storageKey);
        window.sessionStorage.removeItem(storageKey);
        storageRef.current = null;
        return;
      }

      const remember = options?.remember ?? storageRef.current === window.localStorage;
      const targetStorage = remember ? window.localStorage : window.sessionStorage;
      const otherStorage = remember ? window.sessionStorage : window.localStorage;

      targetStorage.setItem(storageKey, JSON.stringify(nextState));
      otherStorage.removeItem(storageKey);
      storageRef.current = targetStorage;
    },
    [sanitizeUser]
  );

  const [userManagementEnabled, setUserManagementEnabled] = useState(true);
  const [userManagementResolved, setUserManagementResolved] = useState(false);
  const refreshPromiseRef = useRef<Promise<AuthState | null> | null>(null);

  const applyUserManagementEnabled = useCallback(
    (enabled: boolean) => {
      setUserManagementEnabled((previous) => {
        if (previous === enabled) {
          return previous;
        }
        if (!enabled) {
          setSession({ user: ADMIN_SESSION_USER, tokens: null });
        } else {
          setSession(null);
        }
        return enabled;
      });
      setUserManagementResolved(true);
    },
    [setSession]
  );

  const apiBase = useMemo(() => {
    const ensureLeadingSlash = (value: string) =>
      value.startsWith("/") ? value : `/${value}`;
    const ensureTrailingSlash = (value: string) =>
      value.endsWith("/") ? value : `${value}/`;
    const normalizeBase = (value: string) => {
      const trimmed = value.trim();
      if (!trimmed || trimmed === ".") {
        return "/";
      }
      return ensureTrailingSlash(ensureLeadingSlash(trimmed));
    };

    const fallback = normalizeBase(import.meta.env.BASE_URL ?? "/");

    if (typeof window === "undefined") {
      return fallback;
    }

    const { pathname, href } = window.location;

    if (pathname.startsWith("/webrtc/")) {
      return "/webrtc/";
    }

    const hostPrefixMatch = pathname.match(
      /^\/([^/]+:[0-9]+|[^/]+\.[^/]+)(?:\/|$)/u
    );
    if (hostPrefixMatch?.[1]) {
      return ensureTrailingSlash(`/${hostPrefixMatch[1]}`);
    }

    const directoryBase = normalizeBase(new URL(".", href).pathname);
    return directoryBase || fallback;
  }, []);

  const resolveApiPath = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        return apiBase;
      }

      if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)) {
        return trimmed;
      }

      if (apiBase !== "/" && trimmed.startsWith(apiBase)) {
        return trimmed;
      }

      const normalized = trimmed.replace(/^\/+/u, "");
      if (apiBase === "/") {
        return `/${normalized}`;
      }
      return `${apiBase}${normalized}`;
    },
    [apiBase]
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadUserManagementState(resolveApiPath("/api/serverConfig"), controller.signal)
      .then((enabled) => {
        if (!controller.signal.aborted) applyUserManagementEnabled(enabled);
      })
      .catch(() => {
        // Cleanup cancels both the pending fetch and delayed startup retries.
      });

    return () => {
      controller.abort();
    };
  }, [applyUserManagementEnabled, resolveApiPath]);

  const logout = useCallback(async () => {
    if (!userManagementEnabled) {
      setSession({ user: ADMIN_SESSION_USER, tokens: null });
      return;
    }

    if (state.tokens?.refreshToken) {
      try {
        await fetch(resolveApiPath("/api/auth/logout"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: state.tokens.refreshToken })
        });
      } catch {
        // Ignore network errors during logout.
      }
    }
    setSession(null);
  }, [resolveApiPath, setSession, state.tokens?.refreshToken, userManagementEnabled]);

  const refreshSession = useCallback(async (): Promise<AuthState | null> => {
    if (!userManagementEnabled) {
      const currentUser = state.user ?? ADMIN_SESSION_USER;
      setSession({ user: currentUser, tokens: null });
      return { user: currentUser, tokens: null };
    }

    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refreshToken = state.tokens?.refreshToken;
    if (!refreshToken) {
      setSession(null);
      return null;
    }

    const refreshPromise = (async (): Promise<AuthState | null> => {
      try {
        const response = await fetch(resolveApiPath("/api/auth/refresh"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken })
        });

        const payload = (await response.json().catch(() => null)) as {
          tokens?: AuthTokens;
          user?: AuthUser;
        } | null;

        if (response.status === 401) {
          setSession(null);
          return null;
        }

        if (!response.ok || !payload?.tokens?.accessToken || !payload.tokens.refreshToken) {
          return null;
        }

        const nextState: AuthState = {
          user: payload.user ?? null,
          tokens: {
            accessToken: payload.tokens.accessToken,
            refreshToken: payload.tokens.refreshToken
          }
        };
        setSession(nextState);
        return nextState;
      } catch {
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  }, [resolveApiPath, setSession, state.tokens?.refreshToken, state.user, userManagementEnabled]);

  const authorizedFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const resolvedInput =
        typeof input === "string"
          ? resolveApiPath(input)
          : input instanceof URL
            ? input
            : input;
      if (!userManagementEnabled) {
        const response = await fetch(resolvedInput, init);
        if (response.status === 401) {
          applyUserManagementEnabled(true);
        }
        return response;
      }
      const perform = async (token?: string) => {
        const headers = new Headers(init?.headers ?? undefined);
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return fetch(resolvedInput, { ...init, headers });
      };

      let response = await perform(state.tokens?.accessToken ?? undefined);
      if (response.status !== 401) {
        return response;
      }

      const refreshed = await refreshSession();
      if (!refreshed?.tokens?.accessToken) {
        return response;
      }

      response = await perform(refreshed.tokens.accessToken);
      return response;
    },
    [applyUserManagementEnabled, refreshSession, resolveApiPath, state.tokens?.accessToken, userManagementEnabled]
  );

  const value = useMemo(
    () => ({
      user: state.user,
      tokens: state.tokens,
      setSession,
      logout,
      authorizedFetch,
      resolveApiPath,
      userManagementEnabled,
      userManagementResolved,
      applyUserManagementEnabled
    }),
    [
      state.user,
      state.tokens,
      setSession,
      logout,
      authorizedFetch,
      resolveApiPath,
      userManagementEnabled,
      userManagementResolved,
      applyUserManagementEnabled
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
